import {HttpParams} from '@angular/common/http';
import {Dictionary} from '../../../domain/utils/common/dictionary';
import {isArray, isDate, isNil, join} from 'lodash-es';
import {isMoment} from 'moment';

/**
 * Defines how array values are serialized in query parameters.
 *
 * - `repeat` — each value gets its own key: `?ids=1&ids=2&ids=3`
 * - `comma`  — values are joined in a single key: `?ids=1,2,3`
 */
export type ArrayQueryParamsFormat = 'repeat' | 'comma';

export class HttpParamsUtil {
    public static toHttpParams(originalParams: Dictionary<any> | any,
        arrayQueryParamsFormat: ArrayQueryParamsFormat = 'comma'): HttpParams {
        if (isNil(originalParams)) {
            return new HttpParams();
        }

        return HttpParamsUtil.convertParamsToHttpParams(originalParams, null, arrayQueryParamsFormat);
    }

    public static addToHttpParams(httpParams: HttpParams, moreParams: Dictionary<any> | any): HttpParams {
        if (isNil(moreParams)) {
            return httpParams;
        }

        return HttpParamsUtil.convertParamsToHttpParams(moreParams, httpParams);
    }

    private static convertParamsToHttpParams(params: Dictionary<any> | any,
        existingHttpParams?: HttpParams,
        arrayQueryParamsFormat: ArrayQueryParamsFormat = 'comma'): HttpParams {
        const initialValue: HttpParams = existingHttpParams ?? new HttpParams();

        return Object.getOwnPropertyNames(params)
            .filter((key: string): boolean => {
                const param = params[key];
                return !isNil(param) && param !== '' && !(isArray(param) && param.length === 0);
            })
            .reduce((httpParams: HttpParams, key: string): HttpParams => {
                return HttpParamsUtil.aggregate(httpParams, key, params, arrayQueryParamsFormat);
            }, initialValue);
    }

    private static aggregate(httpParams: HttpParams,
        key: string, params: Dictionary<any>,
        arrayQueryParamsFormat: ArrayQueryParamsFormat = 'comma'): HttpParams {
        const param = params[key];

        if (isDate(param) || isMoment(param)) {
            return httpParams.set(key, param.toISOString());
        }

        if (isArray(param)) {
            switch (arrayQueryParamsFormat) {
                case 'comma':
                    return httpParams.append(key, join(param, ','));
                case 'repeat':
                    return param.reduce((acc: HttpParams, item: any) => {
                        if (isNil(item) || item === '') {
                            return acc;
                        }
                        return acc.append(key, String(item));
                    }, httpParams);
            }
        }

        return httpParams.set(key, param);
    }
}
